import React from 'react';
import { Box, Typography, Slider, Button } from '@mui/material';
import {
   ArrowBack as BackIcon,
   AutoFixHigh as EnhanceIcon,
   Refresh as ResetIcon,
} from '@mui/icons-material';
import { AnalysisResult, EnhancementSettings } from '../types';

const SLIDER_DEFS: { key: keyof EnhancementSettings; label: string; min: number; max: number }[] = [
   { key: 'brightness',    label: 'Brightness',     min: -50, max: 50 },
   { key: 'contrast',      label: 'Contrast',       min: -50, max: 50 },
   { key: 'sharpness',     label: 'Sharpness',      min: -50, max: 50 },
   { key: 'saturation',    label: 'Saturation',     min: -50, max: 50 },
   { key: 'warmth',        label: 'Warmth',         min: -50, max: 50 },
   { key: 'shadows',       label: 'Shadows',        min: -50, max: 50 },
   { key: 'skinSmoothing', label: 'Skin smoothing', min: 0,   max: 50 },
];

const SLIDER_SX = {
   color: '#1D9E75',
   height: 4,
   '& .MuiSlider-thumb': {
      width: 14, height: 14, backgroundColor: '#1D9E75',
      '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(29,158,117,0.2)' },
   },
   '& .MuiSlider-track': { border: 'none' },
   '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.12)' },
};

const ANALYSIS_LABELS: Record<string, string> = {
   exposure: 'Exposure',
   contrast: 'Contrast',
   warmth: 'Color balance',
   saturation: 'Saturation',
   shadows: 'Shadows',
   sharpness: 'Sharpness',
};

interface StepReviewProps {
   analysis: AnalysisResult;
   settings: EnhancementSettings;
   onSettingsChange: (key: keyof EnhancementSettings, value: number) => void;
   onReset: () => void;
   onEnhance: () => void;
   onBack: () => void;
   isModified: boolean;
}

const StepReview: React.FC<StepReviewProps> = ({
   analysis,
   settings,
   onSettingsChange,
   onReset,
   onEnhance,
   onBack,
   isModified,
}) => {
   const formatValue = (v: number) => (v > 0 ? `+${v}` : String(v));

   const analysisEntries = Object.entries(analysis.analysis).filter(
      ([key]) => key !== 'sceneType'
   ) as [string, { level: string; label: string; status: string }][];

   return (
      <Box>
         {/* Analysis findings */}
         <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 1.5, fontSize: '14px' }}>
            What we found
         </Typography>

         <Box sx={{
            background: '#0A0F0C',
            borderRadius: '8px',
            border: '1px solid #253530',
            p: 1.5,
            mb: 3,
         }}>
            {analysisEntries.map(([key, item]) => (
               <Box key={key} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.75,
               }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: item.status === 'good' ? '#1D9E75' : '#C9A84C',
                     }} />
                     <Typography sx={{ fontSize: '13px', color: '#B0BEC5' }}>
                        {ANALYSIS_LABELS[key] || key}
                     </Typography>
                  </Box>
                  <Typography sx={{
                     fontSize: '13px', fontWeight: 500,
                     color: item.status === 'good' ? '#1D9E75' : '#C9A84C',
                  }}>
                     {item.label}
                  </Typography>
               </Box>
            ))}
         </Box>

         {/* Sliders */}
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '14px' }}>
               Recommended settings
            </Typography>
            {isModified && (
               <Box
                  onClick={onReset}
                  sx={{
                     display: 'flex', alignItems: 'center', gap: 0.5,
                     cursor: 'pointer', color: '#B0BEC5', fontSize: '12px',
                     '&:hover': { color: '#FFFFFF' },
                  }}
               >
                  <ResetIcon sx={{ fontSize: 14 }} />
                  Reset
               </Box>
            )}
         </Box>

         <Box sx={{ mb: 3 }}>
            {SLIDER_DEFS.map((def) => (
               <Box key={def.key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.5 }}>
                     <Typography variant="body2" sx={{ color: '#B0BEC5', fontSize: '13px' }}>
                        {def.label}
                     </Typography>
                     <Typography variant="body2" sx={{
                        color: '#FFFFFF', fontSize: '13px', fontWeight: 600,
                        minWidth: 32, textAlign: 'right',
                     }}>
                        {formatValue(settings[def.key])}
                     </Typography>
                  </Box>
                  <Slider
                     size="small"
                     value={settings[def.key]}
                     min={def.min}
                     max={def.max}
                     onChange={(_, v) => onSettingsChange(def.key, v as number)}
                     sx={SLIDER_SX}
                  />
               </Box>
            ))}
         </Box>

         {/* Actions */}
         <Button
            fullWidth
            onClick={onEnhance}
            startIcon={<EnhanceIcon />}
            sx={{
               background: '#1D9E75',
               color: '#FFFFFF',
               borderRadius: '8px',
               py: 1.5,
               fontSize: '15px',
               fontWeight: 600,
               textTransform: 'none',
               '&:hover': { background: '#178a65' },
               mb: 1.5,
            }}
         >
            Enhance with these settings
         </Button>

         <Typography sx={{
            textAlign: 'center', fontSize: '11px', color: '#546E7A', mb: 2,
         }}>
            Identity preserved — only lighting, color, and clarity change
         </Typography>

         <Button
            fullWidth
            onClick={onBack}
            startIcon={<BackIcon />}
            variant="outlined"
            sx={{
               borderColor: '#253530',
               color: '#B0BEC5',
               borderRadius: '8px',
               py: 1,
               fontSize: '13px',
               textTransform: 'none',
               '&:hover': { borderColor: '#1D9E75', color: '#FFFFFF' },
            }}
         >
            Back to photo selection
         </Button>
      </Box>
   );
};

export default StepReview;
